import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  Grid,
  Group,
  ThemeIcon,
  Table,
  Badge,
  Loader,
  Button,
  TextInput,
  Select,
} from "@mantine/core";
import {
  IconCreditCard,
  IconTrendingUp,
  IconTrendingDown,
  IconCalendar,
  IconDownload,
  IconRefresh,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { usePerformRequest } from "../../../lib/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import { DatePickerInput } from "@mantine/dates";

interface LoanPortfolioSummary {
  totalLoans: number;
  approvedLoans: number;
  rejectedLoans: number;
  pendingLoans: number;
  activeLoans: number;
  closedLoans: number;
  totalLoanAmount: number;
  approvedLoanAmount: number;
  totalInterestEarned: number;
  outstandingBalance: number;
  averageLoanAmount: number;
  approvalRate: number;
  repaymentRate: number;
}

interface LoanByProduct {
  productName: string;
  count: number;
  totalAmount: number;
}

interface LoanPortfolioResponse {
  summary: LoanPortfolioSummary;
  loansByProduct: LoanByProduct[];
}

export default function LoanPortfolioReport() {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateRange, setDateRange] = useState<{ startDate?: number; endDate?: number }>({});

  const {
    data: reportData,
    isLoading,
    reloadData,
  } = usePerformRequest<{ data: LoanPortfolioResponse }>({
    method: Endpoints.GetLoanPortfolioReport(
      dateRange.startDate,
      dateRange.endDate
    ).method,
    route: Endpoints.GetLoanPortfolioReport(
      dateRange.startDate,
      dateRange.endDate
    ).route,
  });

  useEffect(() => {
    const getTimestamp = (date: Date | null | string | unknown): number | undefined => {
      if (!date) return undefined;
      if (date instanceof Date) return date.getTime();
      if (typeof date === 'string') {
        const parsed = new Date(date);
        return isNaN(parsed.getTime()) ? undefined : parsed.getTime();
      }
      return undefined;
    };
    const start = getTimestamp(startDate);
    const end = getTimestamp(endDate);
    setDateRange({ startDate: start, endDate: end });
  }, [startDate, endDate]);

  useEffect(() => {
    reloadData();
  }, [dateRange]);

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const summary = reportData?.data?.summary;
  const loansByProduct = reportData?.data?.loansByProduct || [];

  const statCards = summary
    ? [
        {
          label: "Total Loans",
          value: summary.totalLoans.toLocaleString(),
          icon: IconCreditCard,
          color: "#699269",
        },
        {
          label: "Approved Loans",
          value: summary.approvedLoans.toLocaleString(),
          icon: IconTrendingUp,
          color: "#558B2F",
        },
        {
          label: "Active Loans",
          value: summary.activeLoans.toLocaleString(),
          icon: IconCreditCard,
          color: "#33691E",
        },
        {
          label: "Total Loan Amount",
          value: formatCurrency(summary.totalLoanAmount),
          icon: IconTrendingUp,
          color: "#1B5E20",
        },
        {
          label: "Outstanding Balance",
          value: formatCurrency(summary.outstandingBalance),
          icon: IconTrendingDown,
          color: "#0D3E1F",
        },
        {
          label: "Interest Earned",
          value: formatCurrency(summary.totalInterestEarned),
          icon: IconTrendingUp,
          color: "#699269",
        },
      ]
    : [];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Group justify="space-between" align="flex-start">
            <Group gap="md">
              <ThemeIcon size={48} radius="md" style={{ background: "#699269" }}>
                <IconCreditCard size={24} color="white" />
              </ThemeIcon>
              <div>
                <Title order={2} fw={700}>
                  Loan Portfolio Report
                </Title>
                <Text size="sm" c="dimmed">
                  Comprehensive overview of loan portfolio performance
                </Text>
              </div>
            </Group>
            <Group gap="sm">
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={() => reloadData()}
              >
                Refresh
              </Button>
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                disabled
              >
                Export
              </Button>
            </Group>
          </Group>
        </motion.div>

        {/* Date Filters */}
        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Group gap="md">
            <DatePickerInput
              label="Start Date"
              placeholder="Select start date"
              value={startDate}
              onChange={(value) => setStartDate(value as unknown as Date | null)}
              leftSection={<IconCalendar size={16} />}
              clearable
            />
            <DatePickerInput
              label="End Date"
              placeholder="Select end date"
              value={endDate}
              onChange={(value) => setEndDate(value as unknown as Date | null)}
              leftSection={<IconCalendar size={16} />}
              clearable
              minDate={
                startDate
                  ? startDate instanceof Date
                    ? new Date(startDate.getTime() + 86400000)
                    : new Date(new Date(startDate as string).getTime() + 86400000)
                  : undefined
              }
            />
          </Group>
        </Paper>

        {isLoading ? (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Group justify="center">
              <Loader size="lg" />
            </Group>
          </Paper>
        ) : summary ? (
          <>
            {/* Summary Cards */}
            <Grid gutter="md">
              {statCards.map((card, index) => {
                const IconComponent = card.icon;
                return (
                  <Grid.Col key={card.label} span={{ base: 12, sm: 6, md: 4 }}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Paper
                        shadow="sm"
                        p="lg"
                        radius="md"
                        withBorder
                        style={{ height: "100%" }}
                      >
                        <Stack gap="xs">
                          <Group justify="space-between">
                            <Text size="sm" c="dimmed" fw={500}>
                              {card.label}
                            </Text>
                            <ThemeIcon
                              size={32}
                              radius="md"
                              style={{ background: card.color }}
                            >
                              <IconComponent size={18} color="white" />
                            </ThemeIcon>
                          </Group>
                          <Text size="xl" fw={700} style={{ color: card.color }}>
                            {card.value}
                          </Text>
                        </Stack>
                      </Paper>
                    </motion.div>
                  </Grid.Col>
                );
              })}
            </Grid>

            {/* Detailed Stats */}
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Paper shadow="sm" p="lg" radius="md" withBorder>
                  <Title order={4} mb="md">
                    Portfolio Metrics
                  </Title>
                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Approval Rate
                      </Text>
                      <Badge color="green" size="lg">
                        {formatPercentage(summary.approvalRate)}
                      </Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Repayment Rate
                      </Text>
                      <Badge color="blue" size="lg">
                        {formatPercentage(summary.repaymentRate)}
                      </Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Average Loan Amount
                      </Text>
                      <Text fw={600}>{formatCurrency(summary.averageLoanAmount)}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Closed Loans
                      </Text>
                      <Text fw={600}>{summary.closedLoans.toLocaleString()}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Pending Loans
                      </Text>
                      <Badge color="yellow" size="lg">
                        {summary.pendingLoans.toLocaleString()}
                      </Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Rejected Loans
                      </Text>
                      <Badge color="red" size="lg">
                        {summary.rejectedLoans.toLocaleString()}
                      </Badge>
                    </Group>
                  </Stack>
                </Paper>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Paper shadow="sm" p="lg" radius="md" withBorder>
                  <Title order={4} mb="md">
                    Loan Breakdown
                  </Title>
                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Approved Loan Amount
                      </Text>
                      <Text fw={600} c="green">
                        {formatCurrency(summary.approvedLoanAmount)}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Outstanding Balance
                      </Text>
                      <Text fw={600} c="orange">
                        {formatCurrency(summary.outstandingBalance)}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Total Interest Earned
                      </Text>
                      <Text fw={600} c="blue">
                        {formatCurrency(summary.totalInterestEarned)}
                      </Text>
                    </Group>
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>

            {/* Loans by Product */}
            {loansByProduct.length > 0 && (
              <Paper shadow="sm" p="lg" radius="md" withBorder>
                <Title order={4} mb="md">
                  Loans by Product
                </Title>
                <Table.ScrollContainer minWidth={300}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Product Name</Table.Th>
                        <Table.Th>Number of Loans</Table.Th>
                        <Table.Th style={{ textAlign: "right" }}>Total Amount</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {loansByProduct.map((product, index) => (
                        <Table.Tr key={index}>
                          <Table.Td>
                            <Text fw={500}>{product.productName || "Unknown"}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge color="blue">{product.count}</Badge>
                          </Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            <Text fw={600}>{formatCurrency(product.totalAmount)}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Paper>
            )}
          </>
        ) : (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Text c="dimmed" ta="center">
              No data available
            </Text>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

